import type { ConfigurationOperation } from '@festina/configuration-general-types/configuration'
import type { BranchKey } from '@festina-life/api'
import { errorMessageToastContent } from '@festina-life/components'
import { toast } from '@festina-life/ui/feedback'
import { FormikForm } from '@festina-life/ui/formikForms'
import { useTranslate } from '@festina-life/ui/hooks'
import { useClose } from '@festina-life/ui/structure'

import type { OperationWithSourceInfo } from '../../../../uiModels/types'
import { useConfigurationComponentWorkspaces } from '../../../../uiModels/workspace/useConfigurationComponentWorkspaces'
import { useCreateAutomaticWorkspace } from '../../../../uiModels/workspace/useCreateAutomaticWorkspace'
import { OperationsLoadedButton } from '../../../operationsButton/OperationsLoadedButton'
import { RebaseBranchConflicts } from './RebaseBranchConflicts'
import { useManageRebaseBranch } from './useManageRebaseBranch'

type Props = {
  branchKey: BranchKey
  baseBranchKey: BranchKey
  conflicts: OperationWithSourceInfo<ConfigurationOperation>[]
}

export const RebaseBranchForm = ({
  baseBranchKey,
  branchKey,
  conflicts,
}: Props) => {
  const translate = useTranslate()
  const { addOrUpdateOperation } = useConfigurationComponentWorkspaces()
  const { rebase } = useManageRebaseBranch(branchKey)
  const close = useClose()
  const [rebaseConflictsWorkspaceName, createWorkspace] =
    useCreateAutomaticWorkspace({
      workspaceNamePrefix: `rebase-conflicts-from-${baseBranchKey.name}`,
      branchKey,
    })
  const shouldCreateConflictsWorkspace = conflicts.length > 0

  const handleSubmit = async () => {
    try {
      await rebase(shouldCreateConflictsWorkspace)

      if (shouldCreateConflictsWorkspace) {
        const conflictsWorkspaceKey = await createWorkspace({
          shouldSwitchWorkspace: true,
          shouldInvalidateCache: true,
        })

        const addOperations = conflicts.map(
          ({ internalOperation: operation, layer, block }) =>
            addOrUpdateOperation({
              operation,
              layer,
              block,
              workspaceKey: conflictsWorkspaceKey,
            }),
        )

        await Promise.all(addOperations)
      }

      toast({
        variant: 'success',
        title: conflicts.length
          ? translate('branchRebaseForm.rebase.conflicts.success', {
              branchName: branchKey.name,
              baseBranchName: baseBranchKey.name,
            })
          : translate('branchRebaseForm.rebase.noConflicts.success', {
              branchName: branchKey.name,
              baseBranchName: baseBranchKey.name,
            }),
      })
      close()
    } catch (error) {
      toast(
        errorMessageToastContent({
          error,
          translate,
        }),
      )
    }
  }

  return (
    <FormikForm initialValues={{}} onSubmit={handleSubmit}>
      {({ isSubmitting }) => (
        <>
          <RebaseBranchConflicts
            baseBranchKey={baseBranchKey}
            conflicts={conflicts}
            conflictsWorkspaceName={rebaseConflictsWorkspaceName}
          />
          <OperationsLoadedButton
            startIcon="ArrowPath"
            type="submit"
            isLoading={isSubmitting}
          >
            {translate('branchRebaseForm.button.rebase')}
          </OperationsLoadedButton>
        </>
      )}
    </FormikForm>
  )
}
